import React, { useEffect, useState } from "react";
import {
  Dimensions,
  StyleSheet,
  View,
  TouchableOpacity,
  Text,
} from "react-native";
import Image from "@components/common/Image";
import { useSelector } from "react-redux";
import LinearGradient from "react-native-linear-gradient";
import VideoOverlayModal from "@components/VideoOverlayModal";
import Play from "@assets/svg/Play";
import Button from "@components/Button";
import VideoPlayer from "@components/VideoPlayer";
import colors from "@constants/color";
import { isCLApp } from "../../config";
import useGetVideoTime, {
  GetVideoTimeContextProvider,
} from "../../utils/useGetVideoTime";
import handleCMSNavigation from "../../utils/handleCMSNavigation";
import parseTemplate from "../../utils/parseTemplate";
import { shareOnWhatsapp } from "../../utils/shareEntity";
import useBannerCarouselContext from "./BannerCarouselContext";
import BannerSkeleton from "./Skeleton/BannerSkeleton";
import WhatsAppShare from "./WhatsAppShare";
import {
  LEADER_TRAINING,
  OTHER_EVENTS,
  SHARING_EVENTS,
} from "../../constants/analyticsEventsCT";
import Video from "react-native-video";
import analytics from "@services/analytics";
import { useIsFocused } from "@react-navigation/native";
import YouTubePlayer from "@components/YouTubePlayer";
import ViewShot from "react-native-view-shot";
import Toast from "react-native-simple-toast";
import entityStyle from "@utils/entityStyle";
import useLeaderState from "@hooks/useLeaderState";

const { width } = Dimensions.get("window");

const DemoContainer = ({ children }) => {
  return <View style={{ position: "relative" }}>{children}</View>;
};

const MainContainer = ({ children, imageHeight, shouldShowCTA }) => {
  const [, setState] = useBannerCarouselContext();
  const [isLayoutSet, setIsLayoutSet] = useState(false);

  const onLayout = (event) => {
    setState((s) => ({
      ...s,
      height: imageHeight + (shouldShowCTA ? 60 : 0),
      cta: shouldShowCTA,
    }));
    setIsLayoutSet(true);
  };

  return (
    <View
      style={{ position: "relative" }}
      onLayout={!isLayoutSet ? onLayout : undefined}
    >
      {children}
    </View>
  );
};

const VideoContainer = ({
  video_link = "",
  handleBannerPress = () => {},
  ctaText = "Close",
}) => {
  const [state] = useGetVideoTime();

  return (
    <>
      <VideoPlayer videoLink={video_link} />

      {state.isCompleted && (
        <View style={{ position: "absolute", bottom: 20, left: 20, right: 20 }}>
          <Button
            style={{
              padding: 15,
            }}
            buttonWidth={"100%"}
            btnText={ctaText}
            buttonFontType="head1"
            height={56}
            borderRadius={10}
            onPress={handleBannerPress}
          />
        </View>
      )}
    </>
  );
};

const ImageBackground = (props) => {
  const { children, style = {}, imageStyle, imageRef, ...extra } = props;
  return (
    <View style={style}>
      <Image
        {...extra}
        style={[
          StyleSheet.absoluteFill,
          {
            width: style.width,
            height: style.height,
          },
          imageStyle,
        ]}
      />
      {children}
    </View>
  );
};

const Banner = (props) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  let bottomShadow = false;
  // const { canOpenHttpUrls = false } = useSelector((s) => s.misc);

  const {
    title_ref,
    cms_item_id = "",
    flatBanner = false,
    image_link = "",
    link = "",
    link_type = "",
    sharingTemplate: { image_link: sharing_image_link = "", template = "" },
    sharingLink = "",
    video_link = "",
    sub_type = "",
    image_dimension_width = 1,
    image_dimension_height = 1,
    from_banner_list = false,
    fromBannerCarousel = false,
    text_ctx_cx = "",
    text_ctx_cl = "",
    trackEvent = () => {},
    fromVideoTraining = false,
    overlay_template_name = "",
    transparent_url = "",
    onCapture = () => {},
    captureRef = {},
    customEvent = "",
    customEventData = {},
    userPhone = "",
  } = props;

  let viewShotRef = React.useRef();
  const aspectRatio = image_dimension_width / image_dimension_height;
  const widthMinusPadding = width - 40;
  const imageHeight = widthMinusPadding / aspectRatio;

  const { canLeaderUseApp } = useLeaderState();

  const handleShareOnWhatsapp = async () => {
    if (!canLeaderUseApp()) {
      return;
    }
    const message = parseTemplate(template, {});
    shareOnWhatsapp({
      message: `${message}\n${sharingLink}`,
      image: sharing_image_link,
    });
  };

  let k = sub_type;

  if (k === "youtube_video") {
    return <YouTubePlayer {...props} />;
  }

  const shouldShowCTA =
    (k === "full_background_without_share" && !flatBanner) ||
    (k === "full_background" && !flatBanner) ||
    (k === "contain_background" && !flatBanner) ||
    (k === "youtube_video" && !flatBanner);

  let MainWrapper =
    (!link && !overlay_template_name) || flatBanner ? View : TouchableOpacity;

  if (k === "full_background_no_cta_direct_share") {
    MainWrapper = TouchableOpacity;
  }

  const handleBannerPress = async () => {
    setShow(false);

    if (customEvent) {
      analytics.trackEvent(customEvent, customEventData);
    } else {
      trackEvent(OTHER_EVENTS.BANNER_CLICKED, {
        sub_type,
        cms_item_id,
        cms_title: title_ref,
      });
    }

    if (k === "full_background_no_cta_direct_share") {
      const message = parseTemplate(template, {});
      if (captureRef && typeof captureRef.capture === "function") {
        captureRef.capture().then((uri) => {
          shareOnWhatsapp({
            message: `${message}\n${sharingLink}`,
            image: uri,
            phoneNumber: userPhone,
          });
        });
      } else {
        shareOnWhatsapp({
          message: `${message}\n${sharingLink}`,
          image: sharing_image_link,
        });
      }
      return;
    }

    handleCMSNavigation(link, link_type);
  };

  const handleOverlayPress = () => {
    trackEvent(OTHER_EVENTS.BANNER_CLICKED, {
      sub_type,
      cms_item_id,
      cms_title: title_ref,
    });
    const message = parseTemplate(template, {});
    if (k === "full_background_no_cta_direct_share") {
      if (captureRef && typeof captureRef.capture === "function") {
        captureRef.capture().then((uri) => {
          shareOnWhatsapp({
            message: `${message}\n${sharingLink}`,
            image: uri,
          });
        });
      } else if (
        viewShotRef.current &&
        typeof viewShotRef.current.capture === "function"
      ) {
        viewShotRef.current.capture().then((uri) => {
          shareOnWhatsapp({
            message: `${message}\n${sharingLink}`,
            image: uri,
          });
        });
      } else {
        shareOnWhatsapp({
          message: `${message}\n${sharingLink}`,
          image: sharing_image_link,
        });
      }
    }
  };

  const mainWrapperProps = link
    ? {
        onPress: handleBannerPress,
        useForeground: true,
        activeOpacity: 1,
      }
    : {
        onPress: handleOverlayPress,
      };

  const _toggleModal = () => {
    setShow(!show);
  };

  const imgSource =
    image_link.charAt(0) === "{"
      ? JSON.parse(image_link).file.response.url
      : image_link;

  const MainContainerComponent = fromBannerCarousel
    ? MainContainer
    : DemoContainer;

  const ctaText = fromVideoTraining
    ? "Close"
    : (isCLApp ? text_ctx_cl : text_ctx_cx) || "डीटेल्स देख";

  return (
    <MainContainerComponent
      imageHeight={imageHeight}
      shouldShowCTA={shouldShowCTA}
    >
      <MainWrapper {...mainWrapperProps}>
        <>
          <View
            style={[
              styles.bannerCarouselWrapper,
              {
                paddingHorizontal: flatBanner ? 0 : 10,
                height: flatBanner ? imageHeight * 1.1 : imageHeight,
                marginTop: entityStyle.entityContainer.marginTop,
                marginBottom: flatBanner ? 0 : 16,
              },
            ]}
          >
            {loading && !loaded ? (
              <BannerSkeleton inner={true} height={imageHeight} />
            ) : (
              <></>
            )}

            {overlay_template_name ? (
              <ViewShot
                style={styles.imageBackground}
                ref={viewShotRef}
                options={{ format: "jpg", quality: 0.9 }}
              >
                <ImageBackground
                  source={{
                    uri: imgSource,
                  }}
                  style={[
                    styles.imageBackground,
                    { borderRadius: flatBanner ? 0 : 10 },
                    { height: !loaded ? 0 : imageHeight },
                  ]}
                  onLoadEnd={() => {
                    setLoading(false);
                    setLoaded(true);
                  }}
                >
                  <ImageBackground
                    source={{ uri: transparent_url }}
                    resizeMode="contain"
                    style={[
                      styles.imageBackground,
                      {
                        height: imageHeight,
                        overflow: "hidden",
                      },
                    ]}
                  />
                </ImageBackground>
              </ViewShot>
            ) : (
              <ImageBackground
                source={{
                  uri: imgSource,
                }}
                style={[
                  styles.imageBackground,
                  { borderRadius: flatBanner ? 0 : 10 },
                  { height: !loaded ? 0 : imageHeight },
                ]}
                onLoadEnd={() => {
                  setLoading(false);
                  setLoaded(true);
                }}
              >
                <View style={[styles.bannerCarousel, { height: imageHeight }]}>
                  {bottomShadow && !flatBanner && !video_link && (
                    <LinearGradient
                      colors={[
                        "rgba(0,0,0,0.00)",
                        "rgba(0,0,0,0.03)",
                        "rgba(0,0,0,0.80)",
                      ]}
                      style={{ height: 80, borderRadius: 10 }}
                    />
                  )}

                  {Boolean(video_link) && (
                    <View
                      style={{
                        ...StyleSheet.absoluteFill,
                        overflow: "hidden",
                        borderRadius: 10,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TouchableOpacity
                        testID={`video-${cms_item_id}`}
                        onPress={() => {
                          handleBannerPress();
                          setShow(!show);
                        }}
                        style={{
                          height: 50,
                          width: 50,
                          backgroundColor: "#fff",
                          elevation: 6,
                          borderRadius: 50 / 2,
                          alignItems: "center",
                          justifyContent: "center",
                          paddingLeft: 5,
                        }}
                      >
                        <Play />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              </ImageBackground>
            )}
          </View>
          <VideoOverlayModal
            visible={show}
            onClose={_toggleModal}
            testID={`video_modal-${cms_item_id}`}
            closeTestID={`close_video_modal-${cms_item_id}`}
          >
            <GetVideoTimeContextProvider>
              <VideoContainer {...{ video_link, handleBannerPress, ctaText }} />
            </GetVideoTimeContextProvider>
          </VideoOverlayModal>
        </>
      </MainWrapper>
      <View
        style={{
          height:
            k === "full_background_no_cta"
              ? 0
              : k !== ("full_background_no_cta" && "video_card_with_details") &&
                  !flatBanner
                ? imageHeight / 4 + 8
                : 0,
        }}
      />

      {k === "full_background_without_share" && !flatBanner && (
        <View
          style={{
            position: "absolute",
            flexDirection: "row",
            left: 0,
            right: 0,
            justifyContent: "space-around",
            top: imageHeight + 10,
          }}
        >
          <Button
            style={{
              padding: 15,
            }}
            buttonWidth={width - 30}
            btnText={ctaText}
            buttonFontType="head1"
            height={40}
            borderRadius={10}
            onPress={handleBannerPress}
            borderRadius={60}
          />
        </View>
      )}
      {k === "full_background" && !flatBanner && (
        <View
          style={{
            position: "absolute",
            flexDirection: "row",
            left: 0,
            right: 0,
            justifyContent: "space-around",
            top: imageHeight + 10,
          }}
        >
          <WhatsAppShare
            eventName={SHARING_EVENTS.BANNER_SHARE}
            eventProps={{
              cms_item_id,
              title_ref,
            }}
            title={
              !props.productDetails
                ? "शेयर करें"
                : `आर्डर करवाएं और कमाएंं ₹${props.productDetails.price_tl_commission}`
            }
            semiBold={true}
            onShare={handleShareOnWhatsapp}
            wrapperStyles={{
              backgroundColor: from_banner_list ? "#fff" : "#075E54",
              width: width - 30,
            }}
            textStyles={{
              color: from_banner_list ? "#000" : "#fff",
            }}
            fontType="content1"
            caretStroke={from_banner_list ? colors.primary : "#fff"}
          />
        </View>
      )}
      {k === "contain_background" && !flatBanner && (
        <View
          style={{
            position: "absolute",
            justifyContent: "space-evenly",
            top: imageHeight + 10,
            flexDirection: "row",
            left: 0,
            right: 0,
          }}
        >
          <WhatsAppShare
            title={"शेयर करें"}
            semiBold={true}
            onShare={handleShareOnWhatsapp}
            wrapperStyles={{
              height: 40,
              width: 150,
            }}
          />
          <Button
            style={{
              padding: 15,
            }}
            buttonWidth={150}
            btnText={ctaText}
            buttonFontType="head1"
            height={40}
            borderRadius={50}
            onPress={handleBannerPress}
          />
        </View>
      )}
    </MainContainerComponent>
  );
};

export const VideoBanner = (props) => {
  const { sub_type } = props;
  if (sub_type === "youtube_video") {
    return <YouTubePlayer {...props} />;
  }
  return <Mp4VideoBanner {...props} />;
};

const Mp4VideoBanner = (props) => {
  const { video_link = "", title_ref = "", fromHome = false } = props;
  const [playing, setPlaying] = useState(fromHome ? true : false);
  const [played, setPlayed] = useState(false);
  const isFocused = useIsFocused();
  const { pauseAllVideoBanners } = useSelector((s) => s.misc);

  useEffect(() => {
    if (playing && !played) {
      setPlayed(true);
      analytics.trackEvent(LEADER_TRAINING.VIDEO_PLAYED, {
        video_name: title_ref,
      });
    }
  }, [played, playing, title_ref]);

  useEffect(() => {
    if (!isFocused) {
      setPlaying(false);
    }
  }, [isFocused]);

  useEffect(() => {
    setPlaying(false);
  }, [pauseAllVideoBanners]);

  return video_link ? (
    <>
      <TouchableOpacity
        style={{
          width: "100%",
          alignContent: "center",
          paddingHorizontal: 16,
          position: "relative",
        }}
        onPress={() => {
          setPlaying((s) => !s);
        }}
      >
        <Video
          paused={!playing}
          repeat={true}
          resizeMode="cover"
          source={{
            uri: video_link,
          }}
          style={{
            backgroundColor: "#000",
            height: width * (9 / 17.5),
            borderRadius: 10,
          }}
        />
        <View
          style={{
            ...StyleSheet.absoluteFill,
            backgroundColor: "rgba(0, 0, 0, .5)",
            overflow: "hidden",
            borderRadius: 10,
            alignItems: "center",
            justifyContent: "center",
            opacity: playing ? 0 : 1,
            marginHorizontal: 15,
          }}
        >
          <TouchableOpacity
            onPress={() => setPlaying((p) => !p)}
            style={{
              height: 50,
              width: 50,
              backgroundColor: "#fff",
              elevation: 6,
              borderRadius: 50 / 2,
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: 5,
            }}
          >
            <Play />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </>
  ) : (
    <></>
  );
};

const styles = StyleSheet.create({
  bannerCarouselWrapper: {
    width: width,
  },
  imageBackground: {
    flex: 1,
    width: "100%",
    // height: 240,
    justifyContent: "center",
    alignItems: "center",
    opacity: 1,
    overflow: "hidden",
  },
  bannerCarousel: {
    width: "100%",
    // height: 220,
    borderRadius: 10,
    justifyContent: "flex-end",
  },
  shareBtnContainer: {
    flexDirection: "row",
    justifyContent: "space-evenly",
    paddingBottom: 30,
    position: "absolute",
    width: "100%",
  },
});

export default Banner;
